import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import Footer from "@/components/feature/Footer";
import { getActivityById, addActivityComment, reportActivity, deleteActivity, deleteActivityComment, getOrCreatePrivateConversation, getPrivateConversations } from "@/services/activityService";
import { apiActivityToFront } from "@/hooks/useFeed";
import type { ApiActivityDetail, DiscussionMessage } from "@/lib/types";
import { activityTypeMeta, getInitials, avatarClass, } from "@/pages/home/activityMeta";
import ParticipantsPanel from "./components/ParticipantsPanel";
import DiscussionPanel from "./components/DiscussionPanel";
import logoYonyonHeader from "@/assets/logo-yonyon-header-300x100.png";
import ConfirmModal from "../admin/components/ConfirmModal";
import { removeActivityAsModerator } from "@/services/adminService";
import { getBrowserLocation } from "@/lib/geo";
import MobileActionBar from "./components/MobileActionBar";
import MapPanel from "../home/components/MapPanel";
import { useMapActivities } from "@/hooks/activities/useMapActivities";
import { Helmet } from "react-helmet-async";
import { useReprogramActivity, useCreateNewSessionActivity, useCreateRecurringActivities } from "@/hooks/activities/useActivities";
import ScheduleModal from "./components/ScheduleModal";
import RecurrenceModal from "./components/RecurrenceModal";
import SessionsModal from "./components/SessionsModal";



function TopBar() {
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-background-200">
      <div className="px-4 md:px-8 h-14 md:h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full md:w-auto min-w-0">
          <Link
            to="/"
            className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-base"></i>
            Retour
          </Link>

          <Link
            to="/"
            className="
            ml-auto md:ml-0
            flex items-center justify-end
            w-[145px] h-14
            overflow-hidden
            cursor-pointer
            md:w-auto md:overflow-visible
            md:bg-white md:rounded-lg md:px-2 md:py-1
          "
          >
            <img
              src={logoYonyonHeader}
              alt="Yonyon"
              className="
              h-14 max-w-none w-auto
              scale-[1.07] translate-x-[3rem]
              origin-right
              md:scale-100 md:translate-x-0
            "
            />
          </Link>
        </div>
        <Link
          to="/"
          className="hidden md:flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line text-base"></i>
          Créer une activité
        </Link>
      </div>
    </header>
  );
}

export default function ActivityDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading, joinedIds, toggleJoin } = useAuth();

  const navigate = useNavigate();
  const { data: mapActivities = [] } = useMapActivities();
  const reprogramActivityMutation = useReprogramActivity();
  const createNewSessionActivityMutation = useCreateNewSessionActivity();
  const createRecurringActivitiesMutation = useCreateRecurringActivities();

  const [detail, setDetail] = useState<ApiActivityDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [messages, setMessages] = useState<DiscussionMessage[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [activityToCancel, setActivityToCancel] = useState(false);
  const [activityToRemove, setActivityToRemove] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [privateUnreadCount, setPrivateUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState<"about" | "participants" | "map">("about");
  const toastTimer = useRef<number | null>(null);

  const [reprogramOpen, setReprogramOpen] = useState(false);
  const [reprogramStartsAt, setReprogramStartsAt] = useState('');
  const [reprogramEndsAt, setReprogramEndsAt] = useState('');


  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [newSessionStartsAt, setNewSessionStartsAt] = useState('');
  const [newSessionEndsAt, setNewSessionEndsAt] = useState('');


  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const [recurrenceStartsAt, setRecurrenceStartsAt] = useState('');
  const [recurrenceEndsAt, setRecurrenceEndsAt] = useState('');
  const [recurrenceIntervalWeeks, setRecurrenceIntervalWeeks] = useState(1);
  const [recurrenceOccurrences, setRecurrenceOccurrences] = useState(4);
  const [sessionsOpen, setSessionsOpen] = useState(false);

  const showToast = (msg: string) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = window.setTimeout(() => setToast(null), 3200);
  };

  const loadDetail = useCallback(async () => {
    if (!id) return;
    if (authLoading) return;
    setLoadingDetail(true);
    setDetailError(null);
    try {
      const { latitude, longitude } = await getBrowserLocation();

      const data = await getActivityById(id, latitude, longitude);
      setDetail(data);

      // Convert API comments to DiscussionMessage format
      const msgs: DiscussionMessage[] = (data.comments ?? []).map((c) => ({
        id: c.id,
        author: c.author?.displayName ?? "Anonyme",
        text: c.content,
        time: new Date(c.createdAt).toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isOwner: c.isOwner,
      }));
      setMessages(msgs);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur de chargement";
      setDetailError(msg);
    } finally {
      setLoadingDetail(false);
    }
  }, [id, authLoading]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    setActiveTab("about");
  }, [id]);


  useEffect(() => {
    if (!detail?.isOwner) return;

    const loadPrivateUnreadCount = async () => {
      try {
        const conversations = await getPrivateConversations();

        const unreadCount = conversations.reduce(
          (total, conversation) => total + conversation.unreadCount,
          0
        );

        setPrivateUnreadCount(unreadCount);
      } catch {
        setPrivateUnreadCount(0);
      }
    };

    loadPrivateUnreadCount();
  }, [detail?.isOwner]);


  const activity = useMemo(() => {
    if (!detail) return null;
    return apiActivityToFront(detail);
  }, [detail]);

  const isJoined =
    activity && user
      ? joinedIds.includes(activity.id) ||
      detail?.participants?.some((p) => p.displayName === user.name) === true
      : false;

  if (loadingDetail) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <TopBar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-foreground-400 animate-spin"></i>
            <p className="mt-3 text-sm text-foreground-500">
              Chargement de l'activité…
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (detailError || !activity || !detail) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <TopBar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">
              Activité introuvable
            </h1>
            <p className="mt-1 text-sm text-foreground-500">
              {detailError ?? "Cette activité n'existe plus ou a été retirée."}
            </p>
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={loadDetail}
                className="px-4 py-2 rounded-md border border-background-300 text-foreground-600 text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Réessayer
              </button>
              <Link
                to="/"
                className="inline-block px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const meta = activityTypeMeta[activity.type];
  const isHelp =
    activity.type === "HELP_REQUEST" || activity.type === "HELP_OFFER";

  // Participants list from API
  const participantNames = (detail.participants ?? [])
    .filter((p) => p.id !== detail.author?.id)
    .map((p) => p.displayName ?? p.name ?? "Anonyme");


  const totalParticipants = detail.participants?.length ?? 0;

  const isFull =
    activity.maxParticipants != null &&
    totalParticipants >= activity.maxParticipants;

  const isOrganizer = detail.isOwner === true;


  const handleToggle = async () => {
    if (!user) {
      showToast("Connecte-toi pour rejoindre cette activité");
      navigate("/auth");
      return;
    }

    const wasJoined = isJoined;

    try {
      await toggleJoin(activity.id, wasJoined);

      // Recharge les données réelles depuis l'API
      await loadDetail();

      showToast(
        wasJoined ? "Inscription annulée" : "Inscrit à cette activité !",
      );
    } catch {
      showToast(
        wasJoined
          ? "Impossible de quitter l'activité"
          : "Impossible de rejoindre l'activité",
      );
    }
  };


  // Ouverture de la modale  de reprogramation
  const handleOpenReprogram = () => {
    setReprogramStartsAt(
      detail?.startsAt
        ? new Date(detail.startsAt).toISOString().slice(0, 16)
        : ''
    );

    setReprogramEndsAt(
      detail?.endsAt
        ? new Date(detail.endsAt).toISOString().slice(0, 16)
        : ''
    );

    setReprogramOpen(true);
  }

  // Ouverture de la modale  d'une nouvelle séssion de programmation
  const handleOpenNewSession = () => {
    setNewSessionStartsAt('');
    setNewSessionEndsAt('');
    setNewSessionOpen(true);
  };


  const handleOpenRecurrence = () => {
    const defaultIntervalWeeks = 1;

    setRecurrenceIntervalWeeks(defaultIntervalWeeks);
    setRecurrenceOccurrences(4);
    setRecurrenceEndsAt('');

    if (lastSession) {
      const nextDate = new Date(
        new Date(lastSession.startsAt).getTime() +
        defaultIntervalWeeks * 7 * 24 * 60 * 60 * 1000
      );

      setRecurrenceStartsAt(
        nextDate.toISOString().slice(0, 16)
      );
    } else {
      setRecurrenceStartsAt('');
    }

    setRecurrenceOpen(true);
  }


  const handleRecurrenceIntervalChange = (weeks: number) => {
    setRecurrenceIntervalWeeks(weeks);

    if (!lastSession) return;

    const nextDate = new Date(
      new Date(lastSession.startsAt).getTime() +
      weeks * 7 * 24 * 60 * 60 * 1000
    );

    setRecurrenceStartsAt(
      nextDate.toISOString().slice(0, 16)
    );
  };

  // Création de la reprogrammation
  const handleReprogram = async () => {
    if (!reprogramStartsAt) {
      showToast("Choisis une date de début");
      return;
    }

    if (
      reprogramEndsAt &&
      new Date(reprogramEndsAt) <= new Date(reprogramStartsAt)
    ) {
      showToast("La date de fin doit être postérieure à la date de début");
      return;
    }

    try {
      const result = await reprogramActivityMutation.mutateAsync({
        id: activity.id,
        startsAt: new Date(reprogramStartsAt).toISOString(),
        endsAt: reprogramEndsAt
          ? new Date(reprogramEndsAt).toISOString()
          : undefined,
      });

      setReprogramOpen(false);

      navigate(`/activite/${result.id}`);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Impossible de reprogrammer l'activité";

      showToast(message);
    }
  };


  // Création de la  nouvelle session de programmation
  const handleCreateNewSession = async () => {
    if (!newSessionStartsAt) {
      showToast("Choisis une date de début");
      return;
    }

    if (
      newSessionEndsAt &&
      new Date(newSessionEndsAt) <= new Date(newSessionStartsAt)
    ) {
      showToast("La date de fin doit être postérieure à la date de début");
      return;
    }

    try {
      const result = await createNewSessionActivityMutation.mutateAsync({
        id: activity.id,
        startsAt: new Date(newSessionStartsAt).toISOString(),
        endsAt: newSessionEndsAt
          ? new Date(newSessionEndsAt).toISOString()
          : undefined,
      });

      setNewSessionOpen(false);

      navigate(`/activite/${result.id}`);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Impossible de créer la nouvelle séance";

      showToast(message);
    }
  };



  const handleCreateRecurringActivities = async () => {
    if (!recurrenceStartsAt) {
      showToast("Choisis une date de début");
      return;
    }

    if (
      recurrenceEndsAt &&
      new Date(recurrenceEndsAt) <= new Date(recurrenceStartsAt)
    ) {
      showToast("La date de fin doit être postérieure à la date de début");
      return;
    }

    try {
      await createRecurringActivitiesMutation.mutateAsync({
        id: activity.id,
        startsAt: new Date(recurrenceStartsAt).toISOString(),
        endsAt: recurrenceEndsAt
          ? new Date(recurrenceEndsAt).toISOString()
          : undefined,
        intervalWeeks: recurrenceIntervalWeeks,
        occurrences: recurrenceOccurrences,
      });

      setRecurrenceOpen(false);

      showToast("Les séances récurrentes ont été créées");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Impossible de créer les séances récurrentes";

      showToast(message);
    }
  };

  // Ouvre la confirmation avant l'annulation de l'activité. 
  const handleCancelActivity = () => {
    setActivityToCancel(true);
  };



  // Annule réellement l'activité après confirmation.
  const confirmCancelActivity = async () => {
    try {
      await deleteActivity(activity.id);
      setActivityToCancel(false);
      showToast("Activité annulée");
      navigate("/");
    } catch {
      showToast("Impossible d'annuler l'activité");
    }
  }

  // Ouvre la confirmation avant le retrait de l'activité.
  // Cette action est affichée uniquement aux Admin/Moderator et ne retire encore rien.
  const handleRemoveActivityAsModerator = () => {
    setActivityToRemove(true);
  };

  // Retire l'activité après confirmation.
  // L'autorisation Admin/Moderator sera obligatoirement vérifiée par le backend.
  // Retire l'activité après confirmation.
  // L'autorisation Admin/Moderator est obligatoirement vérifiée par le backend.
  const confirmRemoveActivityAsModerator = async () => {
    try {
      await removeActivityAsModerator(activity.id);

      setActivityToRemove(false);
      showToast("Activité retirée");
      navigate("/");
    } catch {
      showToast("Impossible de retirer l'activité");
    }
  };

  const handleContact = async () => {
    if (!detail || !user) return;

    const organizerId = detail.author?.id;

    if (!organizerId) {
      showToast("Impossible de contacter l'organisateur.");
      return;
    }

    try {
      const conversation = await getOrCreatePrivateConversation(
        detail.id,
        organizerId
      );

      navigate(`/messages/${conversation.id}`);
    } catch {
      showToast("Impossible d'ouvrir la conversation.");
    }
  };


  const handleShare = async () => {
    const shareData = {
      title: detail?.title ?? 'Activité YonYon',
      url: `https://yonyon.fr/activite/${detail?.id}`,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      await navigator.clipboard.writeText(shareData.url);
      showToast('Lien copié dans le presse-papier');
    } catch {
      // Annulation du partage par l'utilisateur
    }
  };
  const handleReport = async () => {
    if (!user) {
      showToast("Connecte-toi pour signaler");
      return;
    }
    try {
      await reportActivity(activity.id, "Contenu inapproprié");
      showToast("Activité signalée à la modération");
    } catch {
      showToast("Erreur lors du signalement");
    }
  };


  // Ouvre la confirmation avant la suppression d'un commentaire.
  const handleDeleteComment = (commentId: string) => {
    setCommentToDelete(commentId);
  };

  // Supprime réellement le commentaire confirmé.
  // Le backend autorise l'auteur du commentaire ainsi que les Admin/Moderator.
  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      await deleteActivityComment(activity.id, commentToDelete);

      setMessages((prev) =>
        prev.filter((message) => message.id !== commentToDelete)
      );

      setCommentToDelete(null);
      showToast("Commentaire supprimé");
    } catch {
      showToast("Impossible de supprimer le commentaire");
    }
  };

  const handleSend = async (text: string) => {
    const tempId = `temp-${Date.now()}`;
    const tempMessage: DiscussionMessage = {
      id: tempId,
      author: user?.name ?? "Moi",
      text,
      time: "À l'instant",
      isOwner: true,
    };
    setMessages((prev) => [...prev, tempMessage]);
    try {
      const newComment = await addActivityComment(activity.id, text);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? {
              id: newComment.id,
              author: newComment.author?.displayName ?? "Moi",
              text: newComment.content,
              time: "À l'instant",
              isOwner: true
            }
            : m,
        ),
      );
    } catch {
      // Keep the optimistic message in state
    }
  };


  const upcomingSessions = detail?.sessions?.filter(
    (session) => new Date(session.startsAt) >= new Date()
  ) ?? [];



  const hasSessions = (detail?.sessions?.length ?? 0) > 0;


  const lastSession = [...(detail?.sessions ?? [])]
    .sort(
      (a, b) =>
        new Date(b.startsAt).getTime() -
        new Date(a.startsAt).getTime()
    )[0];

  const minRecurrenceStartsAt = lastSession
    ? new Date(lastSession.startsAt).toISOString().slice(0, 16)
    : undefined;

  return (
    <>
      {detail && (
        <Helmet>
          <title>{detail?.title ? `${detail.title} — YonYon` : "YonYon"}</title>

          <meta
            name="description"
            content={`Découvre l'activité « ${detail.title} » sur YonYon.`}
          />

          <meta property="og:type" content="website" />
          <meta property="og:locale" content="fr_FR" />
          <meta property="og:site_name" content="YonYon" />
          <meta property="og:title" content={detail.title} />
          <meta
            property="og:url"
            content={`https://yonyon.fr/activite/${detail.id}`}
          />
          <meta
            property="og:image"
            content="https://yonyon.fr/yonyon-partage.png"
          />
          <meta
            property="og:image:alt"
            content="YonYon — Une application qui te reconnecte avec les gens autour de toi"
          />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={detail.title} />
          <meta
            name="twitter:image"
            content="https://yonyon.fr/yonyon-partage.png"
          />
        </Helmet>
      )}



      <div className="min-h-screen flex flex-col bg-background-50 md:pb-0">
        <TopBar />

        <main className="flex-1 w-full px-[0.5rem] md:px-8 pt-6 pb-[183px] md:pt-8 md:pb-8">
          <div className="max-w-5xl mx-auto w-full min-h-0 flex flex-col">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-400">
              <Link
                to="/"
                className="hover:text-foreground-700 transition-colors cursor-pointer"
              >
                Accueil
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span>Activités</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600 truncate">
                {activity.title}
              </span>
            </nav>

            {/* Hero card */}
            <section className={`mt-3 bg-white border border-background-200 rounded-lg p-3 md:p-4 ${activeTab === "about" ? "block" : "hidden md:block"}`}>
              <div
                className={`flex flex-col md:grid md:items-start gap-4 ${hasSessions
                  ? "md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_13rem]"
                  : "md:grid-cols-[minmax(0,1fr)_13rem]"
                  }`}
              >
                <div className="flex-1 min-w-0">
                  {/* Catégorie + état + heure */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.badgeClass}`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${meta.dotClass}`}
                      ></span>
                      {meta.label}
                    </span>

                    {isFull && !isJoined && (
                      <span className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-500 text-xs font-semibold">
                        Complet
                      </span>
                    )}

                    <span className="text-xs text-foreground-400">
                      {activity.time}
                    </span>
                  </div>

                  {/* Titre */}
                  <h1 className="mt-1.5 font-heading text-xl md:text-2xl font-semibold text-foreground-950 leading-tight">
                    {activity.title}
                  </h1>

                  {/* Infos */}
                  <div className="mt-1.5 text-xs text-foreground-500">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line text-foreground-400"></i>
                        {activity.location}
                      </span>
                    </div>

                    <div className="mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1">
                        <i className="ri-navigation-line text-foreground-400"></i>
                        {activity.distanceKm.toLocaleString("fr-FR")} km
                      </span>

                      {!isHelp && (
                        <span className="flex items-center gap-1">
                          <i className="ri-team-line text-foreground-400"></i>
                          {totalParticipants}
                          {activity.maxParticipants != null &&
                            `/${activity.maxParticipants}`}{" "}
                          participants
                        </span>
                      )}
                    </div>
                  </div>


                  {/* Tags */}
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {activity.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Organisateur + actions mobiles */}
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`w-9 h-9 shrink-0 rounded-full ${avatarClass(
                          activity.organizer,
                        )} flex items-center justify-center text-xs font-bold`}
                      >
                        {getInitials(activity.organizer)}
                      </span>

                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-900 truncate">
                          {activity.organizer}
                        </p>
                        <p className="text-xs text-foreground-400">
                          {detail.author?.displayName === "YonYon"
                            ? "Publié par"
                            : "Organisé par"}
                        </p>
                      </div>
                    </div>

                    {/* Actions uniquement sur mobile */}
                    <div className="flex md:hidden shrink-0 flex-col items-end gap-[0.15rem]">
                      {isOrganizer && (
                        <>
                          <div className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-[13px] font-semibold whitespace-nowrap">
                            <i className="ri-user-star-line mr-1"></i>
                            Organisateur
                          </div>

                          <button
                            type="button"
                            onClick={handleCancelActivity}
                            className="px-2.5 py-1 rounded-md border border-red-300 text-red-600 text-[13px] font-semibold hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Annuler l'activité
                          </button>
                        </>
                      )}

                      {!isOrganizer && isJoined && (
                        <>

                          <button
                            type="button"
                            onClick={handleReport}
                            className="px-2.5 py-1 rounded-md text-[11px] font-medium text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-flag-line mr-1"></i>
                            Signaler
                          </button>

                          <div className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-[11px] font-semibold whitespace-nowrap">
                            <i className="ri-checkbox-circle-line mr-1"></i>
                            Inscrit
                          </div>

                          <button
                            type="button"
                            onClick={handleToggle}
                            className="px-2.5 py-1 rounded-md border border-background-300 text-foreground-700 text-[11px] font-semibold hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Quitter l'activité
                          </button>
                        </>
                      )}

                      {!isOrganizer && !isJoined && (
                        <button
                          type="button"
                          onClick={handleReport}
                          className="px-2.5 py-1 rounded-md text-[11px] font-medium text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-flag-line mr-1"></i>
                          Signaler
                        </button>
                      )}
                    </div>
                  </div>

                </div>

                {/* Sessions */}
                {detail?.sessions && detail.sessions.length > 0 && (
                  <section className="w-full md:self-start mt-0 md:max-w-[300px]">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <i className="ri-repeat-line text-foreground-400"></i>
                        <h3 className="text-sm font-semibold text-foreground-800">
                          Séances
                        </h3>
                      </div>

                      {upcomingSessions.length > 4 && (
                        <button
                          type="button"
                          onClick={() => setSessionsOpen(true)}
                          className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                        >
                          Voir plus
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 md:flex md:flex-col">
                      {upcomingSessions
                        .slice(0, 4)
                        .map((session) => (
                          <div
                            key={session.id}
                            className="flex items-center justify-between rounded-lg bg-background-50 px-2 py-[0.2rem]"
                          >
                            <span className="text-sm text-foreground-700 max-[375px]:text-[0.8rem] max-[344px]:text-[0.7rem]">
                              {new Date(session.startsAt).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                              })}
                              {' · '}
                              {new Date(session.startsAt).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>

                            {session.endsAt && (
                              <span className="text-xs text-foreground-400 max-[375px]:text-[0.8rem] max-[344px]:text-[0.7rem]">
                                →{' '}
                                {new Date(session.endsAt).toLocaleTimeString('fr-FR', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                  </section>
                )}

                {/*Actions */}
                <div className="hidden md:flex w-full flex-col gap-1.5">

                  {user && !isOrganizer && (user.isAdmin || user.isModerator) && (
                    <button
                      type="button"
                      onClick={handleRemoveActivityAsModerator}
                      className="text-sm font-medium text-red-600 hover:text-red-700"
                    >
                      Retirer l’activité
                    </button>
                  )}

                  {isOrganizer ? (
                    <>
                      <div className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-accent-100 text-accent-800 text-sm font-semibold">
                        <i className="ri-user-star-line"></i>
                        Organisateur
                      </div>

                      <button
                        type="button"
                        onClick={() => navigate("/messages")}
                        className="w-full px-3 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-message-3-line mr-1"></i>
                        Messages privés{privateUnreadCount > 0 && ` (${privateUnreadCount})`}
                      </button>

                      <button
                        type="button"
                        onClick={handleCancelActivity}
                        className="w-full px-3 py-2 rounded-md border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Annuler l'activité
                      </button>
                    </>
                  ) : isHelp ? (
                    <button
                      type="button"
                      onClick={handleContact}
                      className="w-full px-3 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {activity.type === "HELP_REQUEST"
                        ? "Proposer mon aide"
                        : "Contacter en privé"}
                    </button>
                  ) : isJoined ? (
                    <>
                      <div className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-accent-100 text-accent-800 text-sm font-semibold">
                        <i className="ri-checkbox-circle-line"></i>
                        Inscrit
                      </div>

                      <button
                        type="button"
                        onClick={handleContact}
                        className="w-full px-3 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-message-3-line mr-1"></i>
                        Contacter l'organisateur
                      </button>

                      <button
                        type="button"
                        onClick={handleToggle}
                        className="w-full px-3 py-2 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Quitter l'activité
                      </button>
                    </>
                  ) : isFull ? (
                    <button
                      type="button"
                      disabled
                      className="w-full px-3 py-2.5 rounded-md bg-background-200 text-foreground-400 text-sm font-semibold cursor-not-allowed whitespace-nowrap"
                    >
                      Complet
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleToggle}
                      className="w-full px-3 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Rejoindre
                    </button>
                  )}

                  {/* Partager */}
                  <button
                    type="button"
                    onClick={handleShare}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-line"></i>
                    Partager
                  </button>

                  {/* Signaler */}
                  {!isOrganizer && (
                    <button
                      type="button"
                      onClick={handleReport}
                      className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-flag-line"></i>
                      Signaler
                    </button>
                  )}
                </div>
              </div>

              {/* Actions de programmation */}
              {isOrganizer && (

                <div className="mt-4 md:mt-2 flex items-center justify-center gap-2 md:gap-3 max-[430px]:gap-x-[0.3rem]">
                  <button
                    type="button"
                    onClick={handleOpenReprogram}
                    className="flex-1 md:flex-none md:min-w-[170px] px-[.5rem] max-[375px]:px-[0.4rem] py-2 rounded-md bg-primary-500 text-white text-[0.7rem] leading-4 font-normal md:text-sm md:font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-calendar-event-line mr-1"></i>
                    Reprogrammer
                  </button>

                  <button
                    type="button"
                    onClick={handleOpenNewSession}
                    className="flex-1 md:flex-none md:min-w-[170px] px-[.5rem] max-[375px]:px-[0.4rem] py-2 rounded-md bg-primary-500 text-white text-[0.7rem] leading-4 font-normal md:text-sm md:font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line mr-1"></i>
                    Nouvelle séance
                  </button>

                  <button
                    type="button"
                    onClick={handleOpenRecurrence}
                    className="flex-1 md:flex-none md:min-w-[170px] px-[.5rem] max-[375px]:px-[0.4rem] py-2 rounded-md bg-primary-500 text-white text-[0.7rem] leading-4 font-normal md:text-sm md:font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-repeat-line mr-1"></i>
                    Récurrence
                  </button>
                </div>
              )}

            </section>


            {/* Content grid */}
            <div className="mt-0 lg:mt-6 grid grid-cols-1 lg:grid-cols-3 gap-y-2 lg:gap-6">
              <div className="lg:col-span-2 flex flex-col gap-6 min-h-0">
                {/* Description */}
                <section
                  className={` min-h-0 bg-white border border-background-200 rounded-lg p-5 ${activeTab === "about" ? "block" : "hidden"} lg:block`}
                >

                  <h2
                    className="
                      font-heading
                      text-lg
                      font-normal
                      text-[#544e44]
                      bg-[#f5efe7]
                      px-[0.775rem]
                      py-[0.2rem]
                      rounded-[4px]
                    "
                  >
                    Description
                  </h2>

                  <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                    {activity.description}
                  </p>
                </section>


                <DiscussionPanel
                  messages={messages}
                  onSend={handleSend}
                  onDelete={handleDeleteComment}
                  canModerate={!!user && (user.isAdmin || user.isModerator)}
                  mobileActions={
                    <MobileActionBar
                      onAbout={() => setActiveTab("about")}
                      primaryLabel={
                        isOrganizer
                          ? `Messages privés${privateUnreadCount > 0 ? ` (${privateUnreadCount})` : ""}`
                          : isHelp
                            ? activity.type === "HELP_REQUEST"
                              ? "Proposer mon aide"
                              : "Contacter en privé"
                            : isJoined
                              ? "Contacter l'organisateur"
                              : isFull
                                ? "Complet"
                                : "Rejoindre"
                      }
                      primaryIcon={
                        isOrganizer
                          ? "ri-message-3-line"
                          : isHelp
                            ? "ri-message-3-line"
                            : isJoined
                              ? "ri-message-3-line"
                              : isFull
                                ? "ri-close-circle-line"
                                : "ri-add-line"
                      }
                      primaryDisabled={isFull && !isJoined && !isOrganizer}
                      onPrimary={
                        isOrganizer
                          ? () => navigate("/messages")
                          : isHelp
                            ? handleContact
                            : isJoined
                              ? handleContact
                              : isFull
                                ? () => { }
                                : handleToggle
                      }
                      onParticipants={() => setActiveTab("participants")}
                      onMap={() => setActiveTab("map")}
                      onShare={handleShare}
                      activeTab={activeTab}
                    />
                  }
                />
              </div>

              <div className="lg:col-span-1 flex flex-col gap-[1rem]">
                <div
                  className={
                    activeTab === "participants"
                      ? "block pb-[183px] lg:pb-0"
                      : "hidden lg:block"
                  }
                >
                  <ParticipantsPanel
                    organizer={activity.organizer}
                    participants={participantNames}
                    maxParticipants={activity.maxParticipants}
                    isHelp={isHelp}
                    isPublishedByYonYon={detail.author?.displayName === 'YonYon'}
                  />
                </div>

                {/* Location map */}
                <section
                  className={`bg-white border border-background-200 rounded-lg overflow-hidden ${activeTab === "map" ? "block lg:block" : "hidden lg:block"}`}
                >
                  <div className="relative w-full">
                    <MapPanel
                      onCreateClick={() => { }}
                      activities={mapActivities}
                      highlightActivityId={activity.id}
                      mobileExpanded={activeTab === "map"}
                      showCloseButton
                      showHeader
                      hideHeaderDesktop={false}
                      showCreateCta={false}
                      isDetailPage
                    />
                  </div>
                  <p className="px-4 py-3 text-sm text-foreground-600">
                    {activity.location}
                  </p>
                </section>
              </div>
            </div>
          </div>
        </main >

        {/* Footer */}
        <div className="hidden md:block" >
          <Footer />
        </div >



        <ScheduleModal
          open={reprogramOpen}
          title="Reprogrammer l'activité"
          description="Choisis la nouvelle date et les nouveaux horaires de cette activité."
          startsAt={reprogramStartsAt}
          endsAt={reprogramEndsAt}
          onStartsAtChange={setReprogramStartsAt}
          onEndsAtChange={setReprogramEndsAt}
          onClose={() => setReprogramOpen(false)}
          onSubmit={handleReprogram}
          isPending={reprogramActivityMutation.isPending}
          pendingLabel="Reprogrammation..."
          submitLabel="Reprogrammer"
        />

        <ScheduleModal
          open={newSessionOpen}
          title="Nouvelle séance"
          description="Crée une nouvelle séance indépendante de celle-ci."
          startsAt={newSessionStartsAt}
          endsAt={newSessionEndsAt}
          onStartsAtChange={setNewSessionStartsAt}
          onEndsAtChange={setNewSessionEndsAt}
          onClose={() => setNewSessionOpen(false)}
          onSubmit={handleCreateNewSession}
          isPending={createNewSessionActivityMutation.isPending}
          pendingLabel="Création..."
          submitLabel="Créer la séance"
        />


        <RecurrenceModal
          open={recurrenceOpen}
          startsAt={recurrenceStartsAt}
          endsAt={recurrenceEndsAt}
          minStartsAt={minRecurrenceStartsAt}
          intervalWeeks={recurrenceIntervalWeeks}
          occurrences={recurrenceOccurrences}
          onStartsAtChange={setRecurrenceStartsAt}
          onEndsAtChange={setRecurrenceEndsAt}
          onIntervalWeeksChange={handleRecurrenceIntervalChange}
          onOccurrencesChange={setRecurrenceOccurrences}
          onClose={() => setRecurrenceOpen(false)}
          onSubmit={handleCreateRecurringActivities}
          isPending={createRecurringActivitiesMutation.isPending}
        />


        <SessionsModal
          open={sessionsOpen}
          sessions={upcomingSessions}
          onClose={() => setSessionsOpen(false)}
        />


        {/* Confirmation avant l'annulation d'une activité */}
        < ConfirmModal
          open={activityToCancel}
          title="Annuler cette activité ?"
          message="L'activité sera annulée et ne sera plus visible par les autres utilisateurs."
          confirmLabel="Annuler l'activité"
          tone="danger"
          onConfirm={confirmCancelActivity}
          onCancel={() => setActivityToCancel(false)
          }
        />


        {/* Confirmation avant le retrait d'une activité par un Admin/Moderator */}
        <ConfirmModal
          open={activityToRemove}
          title="Retirer cette activité ?"
          message="Cette activité ne sera plus visible sur l'application."
          confirmLabel="Retirer"
          tone="danger"
          onConfirm={confirmRemoveActivityAsModerator}
          onCancel={() => setActivityToRemove(false)}
        />

        {/* Confirmation de suppression d'un commentaire */}
        <ConfirmModal
          open={commentToDelete !== null}
          title="Supprimer ce commentaire ?"
          message="Ce commentaire sera supprimé définitivement. Cette action est irréversible."
          confirmLabel="Supprimer"
          tone="danger"
          onConfirm={confirmDeleteComment}
          onCancel={() => setCommentToDelete(null)}
        />

        {/* Toast */}
        {
          toast && (
            <div className="fixed bottom-6 left-0 right-0 z-50 flex justify-center animate-scale-in">
              <div className="flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg bg-foreground-900 text-white text-sm font-medium whitespace-nowrap">
                <i className="ri-checkbox-circle-line text-accent-400 text-lg"></i>
                {toast}
              </div>
            </div>
          )
        }
      </div >
    </>
  );
}
